import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { AvatarBubble, ClientPill, ConvTypePill } from "@/components/chatChrome";
import { conversationTitle, peerAvatarUrl } from "@/lib/chatFormat";
import { conversationClientPillName, normalizeChatPresence, type ChatConversation } from "@/lib/chatApi";

type Props = {
  open: boolean;
  conversations: ChatConversation[];
  currentId: number | null;
  selfId: number;
  busy?: boolean;
  onPick: (conversationId: number) => void;
  onClose: () => void;
};

export function ChatForwardPicker({
  open,
  conversations,
  currentId,
  selfId,
  busy = false,
  onPick,
  onClose,
}: Props) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (c.id === currentId) return false;
      if (!q) return true;
      return conversationTitle(c, selfId).toLowerCase().includes(q);
    });
  }, [conversations, currentId, search, selfId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="chat-panel flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border shadow-[var(--chat-shadow)]">
        <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <h3 className="font-semibold">Transférer vers…</h3>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Fermer">
            <X className="size-4" />
          </button>
        </header>
        <div className="border-b border-[var(--chat-border)] px-3 py-2">
          <label className="flex items-center gap-2 rounded-xl bg-[var(--chat-bg)] px-3 py-2">
            <Search className="size-4 text-[var(--chat-muted)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une conversation…"
              autoFocus
              className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--chat-muted)]"
            />
          </label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {list.length === 0 ? (
            <p className="p-3 text-sm text-[var(--chat-muted)]">Aucune conversation.</p>
          ) : (
            list.map((c) => {
              const title = conversationTitle(c, selfId);
              const avatar = peerAvatarUrl(c, selfId);
              const clientPill = conversationClientPillName(c);
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={busy}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--chat-surface-elevated)] disabled:opacity-50"
                  onClick={() => onPick(c.id)}
                >
                  <AvatarBubble
                    label={title}
                    url={avatar}
                    presence={c.type === "dm" ? normalizeChatPresence("offline") : undefined}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <ConvTypePill type={c.type} isRoom={c.is_room} />
                      <span className="truncate text-sm font-medium">{title}</span>
                      {clientPill ? <ClientPill name={clientPill} /> : null}
                    </span>
                  </span>
                  {busy ? <Loader2 className="size-4 animate-spin text-[var(--chat-muted)]" /> : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
